import { NarrowPageLayout } from '~/components/PageLayout'
import styled from '@emotion/styled'
import { Alert, device, H2, space, Text, color } from '@ticketswap/solar'
import BrowseButtons from './BrowseButtons'
import dynamic from 'next/dynamic'
import { Skeleton } from '~/components/ListCard/Skeleton'
import ErrorBoundary from '~/components/ErrorBoundary'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import DiscoverNearby from '~/components/DiscoverNearby'
import DiscoverCategories from '~/components/DiscoverCategories'
import { Translate } from '~/types/Translation'

const FollowedEvents = dynamic(() => import('./FollowedEvents'), {
  ssr: false,
  loading: () => <Skeleton />,
})

const DiscoverFeed = dynamic(() => import('~/components/DiscoverFeed'), {
  ssr: false,
  loading: () => <Skeleton count={2} />,
})

const AppsBanner = dynamic(() => import('~/components/DiscoverAppsBanner'), {
  ssr: false,
  loading: () => <Skeleton />,
})

const TrustFactors = dynamic(() => import('./TrustFactors'), {
  ssr: false,
  loading: () => null,
})

const PreferredCityBanner = dynamic(() => import('./PreferredCityBanner'), {
  ssr: false,
  loading: () => null,
})

const StyledNarrowPageLayout = styled(NarrowPageLayout)`
  padding-block-start: ${space[32]};
  padding-block-end: ${space[48]};

  @media ${device.tablet} {
    padding-block-end: ${space[64]};
  }
`

const StyledH2 = styled(H2)`
  margin-block-start: ${space[44]};
`

const DiscoverLayout = styled.div`
  display: grid;
  grid-gap: ${space[16]};
  grid-template-columns: minmax(0, 1fr);

  @media ${device.tablet} {
    grid-gap: ${space[24]};
  }
`

const StyledText = styled(Text)`
  color: ${color.foregroundMuted};
  margin-block-end: ${space[16]};
`

interface Props {
  t: Translate
}

const HomePageContent = ({ t }: Props) => {
  const { loggedInUser } = useLoggedInUser()

  return (
    <>
      {!loggedInUser && <TrustFactors t={t} />}

      <StyledNarrowPageLayout>
        <BrowseButtons t={t} />

        <StyledH2>{t('home:discover.title')}</StyledH2>
        <StyledText as="p">{t('home:discover.subtitle')}</StyledText>

        <PreferredCityBanner t={t} />

        <ErrorBoundary
          fallback={<Alert variant="warning">{t('common:generic.somethingWentWrong')}</Alert>}
        >
          <DiscoverLayout>
            <AppsBanner />
            <DiscoverFeed t={t} />
            <FollowedEvents t={t} />
            <DiscoverNearby t={t} />
            <DiscoverCategories t={t} />
          </DiscoverLayout>
        </ErrorBoundary>
      </StyledNarrowPageLayout>
    </>
  )
}

export default HomePageContent
